<template>
  <div class="message-template-dialog">
    <el-dialog :visible.sync="visible" :title="title" custom-class="with-title" :before-close="beforeClose">
      <div v-loading="loading">
        <!-- テンプレート名-->
        <div class="input-container">
          <div class="label mb-x1">テンプレート名</div>
          <div class="input">
            <el-input
              placeholder="テンプレート名"
              :maxlength="256"
              :class="{ success: $v.name.required && !$v.name.$invalid && name.trim() }"
              type="text"
              v-model="name"
              tabindex="1"
              @input="templateChangeName"
            ></el-input>
          </div>
        </div>
        <!-- 内容-->
        <div class="input-container">
          <div class="label">
            内容
            <div class="sub">{{ formatNumber(bodyLength) }} / {{ formatNumber(maxBodyLength) }}</div>
          </div>
          <editable
            class="body-textarea"
            v-model="body"
            placeholder="内容を入力"
            :highlights="highlights"
            @input="templateChangeBody"
          ></editable>
          <div class="input-invalid-text" v-if="bodyLength > maxBodyLength">文字数は4,000文字以下にしてください。</div>
          <MessageConvert type="templateDialog"></MessageConvert>
        </div>
        <div class="dialog-footer" v-if="isEdit" layout layout-align="space-between">
          <el-button type="default" @click="deleteMessageTemplate"
            ><i class="mdi mdi-delete-forever"></i>削除</el-button
          >
          <el-button type="primary" :disabled="!isFormValid || isSaving" @click="updateMessageTemplate"
            ><i class="mdi mdi-content-save"></i>保存</el-button
          >
        </div>
        <div class="dialog-footer" v-else>
          <el-button type="primary" :disabled="!isFormValid || isSaving" @click="createMessageTemplate"
            ><i class="mdi mdi-content-save"></i>保存</el-button
          >
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.message-template-dialog {
  .input-container {
    margin: 0 0 ($spacing-unit * 3);
  }

  .label {
    position: relative;
    margin: 0 0 $spacing-unit;
    font-size: $font-large-size;

    .sub {
      position: absolute;
      top: 0;
      right: 0;
      color: $sub-text-color;
    }
  }
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import twitterText from 'twitter-text'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import Editable from '@/client/components/_atoms/editable'
import MessageConvert from '@/client/components/_atoms/message_convert'
import api from '@/client/core/api'
import { formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useSettingGroupStore } from '@/client/store/useSettingGroupStore'
import { useSystemStore } from '@/client/store/useSystemStore'

export default defineComponent({
  components: {
    MessageConvert,
    Editable
  },
  data() {
    return {
      title: '',
      visible: false,
      loading: false,
      templateId: 0,
      name: '',
      body: '',
      maxBodyLength: 4000,
      isSaving: false,
      highlights: [
        {
          type: 'info',
          keywords: ['_account_name_', '_screen_name_', '_account_id_', '_serial_code_']
        }
      ],
      rawData: {}
    }
  },
  computed: {
    ...mapState(useSystemStore, {
      currentGroup: state => state.currentGroup
    }),
    /**
     * メッセージ内容の文字数取得
     */
    bodyLength() {
      return twitterText.parseTweet(this.body).weightedLength
    },
    /**
     * Check if is edit
     */
    isEdit() {
      if (this.templateId) {
        return true
      }

      return false
    },
    isFormValid() {
      if (
        this.name &&
        this.name.length <= 256 &&
        this.name.trim() &&
        this.body &&
        this.bodyLength <= this.maxBodyLength &&
        this.body.trim()
      ) {
        return true
      }

      return false
    },
    templateData() {
      return {
        groupId: this.currentGroup?.id,
        name: this.name,
        body: this.body
      }
    },
    isFormChanged() {
      return JSON.stringify(this.templateData) !== JSON.stringify(this.rawData)
    }
  },
  methods: {
    formatNumber,

    ...mapActions(useSettingGroupStore, ['fetchTemplates']),
    /**
     * Open dialog, change title
     * @param {number} templateId
     */
    async open(templateId: number) {
      this.visible = true
      if (templateId) {
        this.templateId = templateId
        this.title = 'テンプレートの編集'
        await this.fetchData()
      } else {
        this.title = 'テンプレートの新規作成'
      }

      this.rawData = this.templateData
    },

    /**
     * Get template by id
     */
    async fetchData() {
      this.loading = true
      const result = await api.get('/message_templates/' + this.templateId)
      this.loading = false

      if (result && result.data) {
        this.name = result.data.name
        this.body = result.data.body
        return
      }

      this.$notify({
        title: '対象のテンプレートはすでに削除されています。',
        message: '',
        customClass: 'danger',
        duration: 5000
      })
      this.reset()

      const { shouldLeaveGroupSetting } = await this.fetchTemplates()

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },

    /**
     * テンプレートの新規作成
     */
    async createMessageTemplate() {
      if (!this.isFormValid) {
        return
      }

      this.isSaving = true
      const params = this.setParams()
      const result = await api.post('/message_templates', params)

      setTimeout(() => {
        this.isSaving = false
      }, 2000)

      if (!result.data) {
        this.$notify({
          title: 'テンプレートの保存に失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        this.reset()
        return
      }

      this.$notify({
        title: 'テンプレートを保存しました。',
        message: '',
        customClass: 'success',
        duration: 5000
      })
      this.reset()

      const { shouldLeaveGroupSetting } = await this.fetchTemplates()

      if (shouldLeaveGroupSetting) {
        this.$router.push('/campaigns')
      }
    },

    /**
     * テンプレートの編集
     */
    async updateMessageTemplate() {
      TrackingService.sendEvent('click:テンプレート|テンプレートの編集|保存')

      if (!this.isFormValid) {
        return
      }

      this.isSaving = true
      const params = this.setParams()
      delete params.groupId

      const result = await api.put('/message_templates/' + this.templateId, params)

      setTimeout(() => {
        this.isSaving = false
      }, 2000)

      if (result && result.data) {
        this.$notify({
          title: 'テンプレートを保存しました。',
          message: '',
          customClass: 'success',
          duration: 5000
        })
        this.reset()

        const { shouldLeaveGroupSetting } = await this.fetchTemplates()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }

      if (result && result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のテンプレートはすでに削除されています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        this.reset()

        const { shouldLeaveGroupSetting } = await this.fetchTemplates()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }

      this.$notify({
        title: 'テンプレートの保存に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
      this.reset()
    },

    /**
     * Set params
     * @returns {{groupId?: number, name: string, body: string}}
     */
    setParams(): { groupId?: number; name: string; body: string } {
      return {
        groupId: this.currentGroup?.id,
        name: this.name,
        body: this.body
      }
    },

    /**
     * Delete message template
     *
     */
    async deleteMessageTemplate() {
      TrackingService.sendEvent('click:テンプレート|テンプレートの編集|削除')

      const confirm = window.confirm('テンプレートを削除してよろしいですか？')
      if (!confirm) {
        return
      }

      const result = await api.delete('/message_templates/' + this.templateId)
      if (result && result.data) {
        this.$notify({
          title: 'テンプレートを削除しました。',
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.reset()

        const { shouldLeaveGroupSetting } = await this.fetchTemplates()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }

      if (result && result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象のテンプレートはすでに削除されています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        this.reset()

        const { shouldLeaveGroupSetting } = await this.fetchTemplates()

        if (shouldLeaveGroupSetting) {
          this.$router.push('/campaigns')
        }

        return
      }

      this.$notify({
        title: 'テンプレートの削除に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
      this.reset()
    },

    /**
     * Reset data before close
     */
    beforeClose() {
      TrackingService.sendEvent('click:テンプレート|テンプレートの編集|閉じる')

      if (!this.isFormChanged) {
        return this.reset()
      }

      const confirm = window.confirm('編集内容が保存されていません。ダイアログを閉じてよろしいですか?')

      if (!confirm) {
        return
      }

      this.reset()
    },

    /**
     * Reset data
     */
    reset() {
      this.templateId = 0
      this.name = ''
      this.body = ''
      this.visible = false
    },

    templateChangeName() {
      TrackingService.sendEvent('input:テンプレート|テンプレートの編集|テンプレート名')
    },

    templateChangeBody() {
      TrackingService.sendEvent('input:テンプレート|テンプレートの編集|内容')
    }
  },
  validations: {
    name: {
      required
    },
    body: {
      required
    }
  }
})
</script>
